import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SEMANTIC_BUCKETS } from "@/lib/statusSemantics";
import { TierBadge } from "@/components/presentation/TierBadge";
import { isDemoUser } from "@/lib/demoMode";
import { useAuth } from "@/context/AuthContext";

// v1.2 Part 2: shared "Insights & Recommendations" feed — reused across
// Admin, Instructor, and Parent dashboards. Each card individually
// answers "what should I do next" (recommendation) or "what's worth
// knowing" (insight/celebration). Sorted server-side: urgent/attention
// first, celebrations last. Shows 2-3 by default with a "View all" toggle
// so the feed never pushes core dashboard tasks further down the page.
const SEVERITY_ORDER = ["urgent", "needs_attention", "waiting", "scheduled_upcoming", "good_completed", "archived_inactive"];

export const InsightsRecommendationsFeed = ({ items, loading }) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const showTier = isDemoUser(user);

  if (loading) {
    return (
      <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-3" data-testid="insights-recommendations-module">
        <Skeleton className="h-4 w-44" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  const list = [...(items || [])].sort(
    (a, b) => SEVERITY_ORDER.indexOf(a.bucketKey) - SEVERITY_ORDER.indexOf(b.bucketKey)
  );
  const visible = expanded ? list : list.slice(0, 3);

  return (
    <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5" data-testid="insights-recommendations-module">
      <h2 className="font-heading text-base sm:text-lg font-semibold mb-3 flex items-center gap-2">
        Insights &amp; Recommendations
        {showTier && <TierBadge tier="elite" size="xs" />}
      </h2>

      {list.length === 0 ? (
        <div className="flex items-start gap-3 rounded-lg bg-muted p-4" data-testid="insights-recommendations-empty-state">
          <CheckCircle2 className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium">You&apos;re in good shape today.</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              No overdue accounts, no aging issues, nothing waiting on you — a great window to follow up with a
              prospective student or get ahead on next week's schedule.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((item) => {
            const sem = SEMANTIC_BUCKETS[item.bucketKey] || SEMANTIC_BUCKETS.waiting;
            const Icon = sem.icon;
            const isRecommendation = item.type === "recommendation";
            return (
              <div
                key={item.id}
                className={`rounded-lg border border-l-4 p-3 sm:p-4 ${sem.leftBorder}`}
                data-testid="insight-recommendation-card"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <Badge
                    variant={isRecommendation ? "outline" : "secondary"}
                    className={`text-[10px] ${isRecommendation ? "border-[hsl(var(--role-accent))] text-[hsl(var(--role-accent))]" : ""}`}
                  >
                    {isRecommendation ? "Recommendation" : "Insight"}
                  </Badge>
                </div>
                <p className="text-sm font-medium" data-testid="insight-recommendation-title">{item.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{item.why}</p>
                {item.next_step && (
                  <Button
                    size="sm"
                    variant="link"
                    className="h-auto px-0 mt-1.5 text-[hsl(var(--role-accent))]"
                    onClick={() => navigate(item.next_step.route)}
                    data-testid="insight-recommendation-primary-action-button"
                  >
                    {item.next_step.label}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {list.length > 3 && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-3 w-full sm:w-auto"
          onClick={() => setExpanded((e) => !e)}
          data-testid="insights-recommendations-view-all-button"
        >
          {expanded ? "Show less" : `View all (${list.length})`}
        </Button>
      )}
    </div>
  );
};
